import {
  TextField,
  Typography,
  Alert,
  Box,
  Paper,
  Button,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useEffect, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { Field } from 'src/API';
import { FieldInput } from 'src/api/process-module';
import logger from 'src/log/log';
import { MarkdownToolbar } from './MarkdownToolbar';
import './markdown-styles.css';

interface MarkdownFieldProps {
  field: FieldInput & Partial<Field>;
  index: number;
  handleFieldChange: (index: number, field: Partial<FieldInput & Partial<Field>>) => void;
  isSubmitting: boolean;
  isUsingExistingField: boolean;
}

// Helper function to parse inline style string to style object
const parseStyleString = (styleString: string | React.CSSProperties): React.CSSProperties => {
  if (typeof styleString !== 'string') {
    return styleString || {};
  }

  const styleObject: React.CSSProperties = {};
  const declarations = styleString.split(';').filter((decl) => decl.trim());

  declarations.forEach((declaration) => {
    const [property, value] = declaration.split(':').map((s) => s.trim());
    if (property && value) {
      const camelCaseProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
      styleObject[camelCaseProperty as any] = value;
    }
  });

  return styleObject;
};

// Markdown rendering components for preview
const getMarkdownComponents = () => ({
  h1: ({ children }: any) => (
    <Typography variant="h5" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold', fontSize: '1.1rem' }}>
      {children}
    </Typography>
  ),
  h2: ({ children }: any) => (
    <Typography variant="h6" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold', fontSize: '1rem' }}>
      {children}
    </Typography>
  ),
  h3: ({ children }: any) => (
    <Typography variant="subtitle1" sx={{ mt: 0.5, mb: 0.5, fontWeight: 'bold', fontSize: '0.95rem' }}>
      {children}
    </Typography>
  ),
  p: ({ children, className, ...props }: any) => {
    const style = props.node?.properties?.style;
    if (style) {
      const parsedStyle = parseStyleString(style);
      return (
        <Typography sx={{ mb: 0.5, fontSize: '0.875rem' }} style={parsedStyle} className={className}>
          {children}
        </Typography>
      );
    }
    return <Typography sx={{ mb: 0.5, fontSize: '0.875rem' }}>{children}</Typography>;
  },
  span: ({ children, className, ...props }: any) => {
    const style = props.node?.properties?.style;
    const parsedStyle = style ? parseStyleString(style) : {};
    return (
      <span style={parsedStyle} className={className}>
        {children}
      </span>
    );
  },
  div: ({ children, className, ...props }: any) => {
    const style = props.node?.properties?.style;
    const parsedStyle = style ? parseStyleString(style) : {};
    return (
      <div style={parsedStyle} className={className}>
        {children}
      </div>
    );
  },
  strong: ({ children, ...props }: any) => {
    const style = props.node?.properties?.style;
    const parsedStyle = style ? parseStyleString(style) : {};
    return <strong style={parsedStyle}>{children}</strong>;
  },
  code: ({ inline, className, children, ...props }: any) => {
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : '';

    return !inline && language ? (
      <Box sx={{ my: 0.5 }}>
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={language}
          PreTag="div"
          customStyle={{
            borderRadius: '4px',
            padding: '8px',
            fontSize: '0.75rem',
            margin: 0,
          }}
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      </Box>
    ) : (
      <code
        className={className}
        style={{
          backgroundColor: '#f5f5f5',
          padding: '2px 4px',
          borderRadius: '3px',
          fontSize: '0.8em',
          fontFamily: 'monospace',
        }}
        {...props}
      >
        {children}
      </code>
    );
  },
  ul: ({ children }: any) => (
    <Box component="ul" sx={{ pl: 2, my: 0.5, fontSize: '0.875rem' }}>
      {children}
    </Box>
  ),
  ol: ({ children }: any) => (
    <Box component="ol" sx={{ pl: 2, my: 0.5, fontSize: '0.875rem' }}>
      {children}
    </Box>
  ),
  li: ({ children }: any) => (
    <Typography component="li" sx={{ mb: 0.25, fontSize: '0.875rem' }}>
      {children}
    </Typography>
  ),
  table: ({ children }: any) => (
    <Box
      component="table"
      sx={{
        width: '100%',
        borderCollapse: 'collapse',
        my: 0.5,
        fontSize: '0.875rem',
        '& th, & td': {
          border: '1px solid #e0e0e0',
          padding: '6px 8px',
          textAlign: 'left',
        },
        '& th': {
          backgroundColor: '#f5f5f5',
          fontWeight: 'bold',
        },
      }}
    >
      {children}
    </Box>
  ),
  thead: ({ children }: any) => <thead>{children}</thead>,
  tbody: ({ children }: any) => <tbody>{children}</tbody>,
  tr: ({ children }: any) => <tr>{children}</tr>,
  th: ({ children }: any) => <th>{children}</th>,
  td: ({ children }: any) => <td>{children}</td>,
  blockquote: ({ children }: any) => (
    <Box
      sx={{
        borderLeft: '3px solid #2196f3',
        pl: 1.5,
        py: 0.5,
        my: 0.5,
        backgroundColor: '#f8fafc',
        borderRadius: '0 4px 4px 0',
        fontSize: '0.875rem',
      }}
    >
      {children}
    </Box>
  ),
  hr: () => (
    <Box
      component="hr"
      sx={{
        my: 1,
        border: 'none',
        borderTop: '1px solid #e0e0e0',
      }}
    />
  ),
});

// Markdown examples with categories
const MARKDOWN_EXAMPLES = [
  {
    category: 'Text Colors',
    examples: [
      {
        name: 'Red Text',
        code: '<span style="color: red;">Red text here</span>',
      },
      {
        name: 'Green Text',
        code: '<span style="color: green;">Green text here</span>',
      },
      {
        name: 'Blue Text',
        code: '<span style="color: blue;">Blue text here</span>',
      },
      {
        name: 'Custom Color',
        code: '<span style="color: #2196f3;">Custom hex color text</span>',
      },
    ],
  },
  {
    category: 'Backgrounds & Highlights',
    examples: [
      {
        name: 'Yellow Highlight',
        code: '<span style="background-color: yellow; padding: 2px 6px;">Highlighted text</span>',
      },
      {
        name: 'Gray Background',
        code: '<span style="background-color: #f0f0f0; padding: 4px 8px; border-radius: 4px;">Gray background</span>',
      },
    ],
  },
  {
    category: 'Alert Boxes',
    examples: [
      {
        name: 'Success Alert',
        code: `<div style="background-color: #d4edda; padding: 15px; border-radius: 8px; border-left: 4px solid #4caf50;">
  <strong style="color: #155724;">✅ Success</strong><br>
  <span style="color: #155724;">Operation completed successfully!</span>
</div>`,
      },
      {
        name: 'Warning Alert',
        code: `<div style="background-color: #fff3cd; padding: 15px; border-radius: 8px; border-left: 4px solid #ffc107;">
  <strong style="color: #856404;">⚠️ Warning</strong><br>
  <span style="color: #856404;">Please review these changes.</span>
</div>`,
      },
      {
        name: 'Error Alert',
        code: `<div style="background-color: #f8d7da; padding: 15px; border-radius: 8px; border-left: 4px solid #dc3545;">
  <strong style="color: #721c24;">❌ Error</strong><br>
  <span style="color: #721c24;">An error occurred.</span>
</div>`,
      },
      {
        name: 'Info Alert',
        code: `<div style="background-color: #d1ecf1; padding: 15px; border-radius: 8px; border-left: 4px solid #17a2b8;">
  <strong style="color: #0c5460;">ℹ️ Info</strong><br>
  <span style="color: #0c5460;">Additional information here.</span>
</div>`,
      },
    ],
  },
  {
    category: 'Buttons',
    examples: [
      {
        name: 'Primary Button',
        code: '<span style="color: white; background-color: #2196f3; padding: 8px 16px; border-radius: 4px; font-weight: bold;">Primary Button</span>',
      },
      {
        name: 'Success Button',
        code: '<span style="color: white; background-color: #4caf50; padding: 8px 16px; border-radius: 4px; font-weight: bold;">Success Button</span>',
      },
    ],
  },
  {
    category: 'Code Blocks',
    examples: [
      {
        name: 'JavaScript Code',
        code: `\`\`\`javascript
const greet = (name) => {
  console.log(\`Hello, \${name}!\`);
};
greet('World');
\`\`\``,
      },
      {
        name: 'Python Code',
        code: `\`\`\`python
def calculate(x, y):
    return x + y

result = calculate(5, 10)
print(result)
\`\`\``,
      },
      {
        name: 'Inline Code',
        code: 'Use `console.log()` for debugging.',
      },
    ],
  },
  {
    category: 'Tables',
    examples: [
      {
        name: 'Simple Table',
        code: `| Header 1 | Header 2 | Header 3 |
|----------|----------|----------|
| Row 1 Col 1 | Row 1 Col 2 | Row 1 Col 3 |
| Row 2 Col 1 | Row 2 Col 2 | Row 2 Col 3 |`,
      },
      {
        name: 'Table with Status',
        code: `| Feature | Status | Priority |
|---------|--------|----------|
| Colors | <span style="color: green;">✅</span> | High |
| Images | <span style="color: green;">✅</span> | High |
| Tables | <span style="color: orange;">🔄</span> | Medium |`,
      },
    ],
  },
  {
    category: 'Lists',
    examples: [
      {
        name: 'Unordered List',
        code: `- First item
- Second item
- Third item`,
      },
      {
        name: 'Ordered List',
        code: `1. First step
2. Second step
3. Third step`,
      },
      {
        name: 'Task List',
        code: `- [x] Completed task
- [ ] Pending task
- [ ] Another pending task`,
      },
    ],
  },
  {
    category: 'Text Formatting',
    examples: [
      {
        name: 'Bold & Italic',
        code: '**Bold text** and *italic text* and ~~strikethrough~~',
      },
      {
        name: 'Headings',
        code: `# Heading 1
## Heading 2
### Heading 3`,
      },
      {
        name: 'Blockquote',
        code: '> This is a blockquote\n> It can span multiple lines',
      },
      {
        name: 'Horizontal Rule',
        code: '---',
      },
    ],
  },
];

export const MarkdownField = ({
  field,
  index,
  handleFieldChange,
  isSubmitting,
  isUsingExistingField,
}: MarkdownFieldProps) => {
  // Use controlled state to track if user has made changes
  const [hasUserChanges, setHasUserChanges] = useState(false);
  const [currentValue, setCurrentValue] = useState('');
  const [error, setError] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [exampleTabs, setExampleTabs] = useState<{ [key: string]: number }>({});
  const textFieldRef = useRef<HTMLInputElement>(null);

  // Enhanced data source resolution with better error handling
  const getFieldDataSource = () => {
    try {
      // Try multiple sources for data
      const sources = [field.dataSource, (field as any).properties?.dataSource, field.properties?.dataSource];

      for (const source of sources) {
        if (source !== null && source !== undefined) {
          return typeof source === 'string' ? source : String(source);
        }
      }

      return '';
    } catch (error) {
      logger.error('Error resolving markdown field data source:', error);
      setError('Error loading field content');
      return '';
    }
  };

  // Initialize value from field data with improved logic
  useEffect(() => {
    if (!hasUserChanges) {
      const initialValue = getFieldDataSource();
      setCurrentValue(initialValue);
      setError(''); // Clear any previous errors

      logger.log('Markdown field initialized:', {
        fieldId: field.id,
        initialValue: initialValue,
        fieldData: {
          dataSource: field.dataSource,
          propertiesDataSource: (field as any).properties?.dataSource,
        },
      });
    }
  }, [field.dataSource, (field as any).properties?.dataSource, field.id, hasUserChanges]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setCurrentValue(newValue);
    setHasUserChanges(true);
    setError('');

    try {
      logger.log('Markdown field saving:', {
        fieldId: field.id,
        contentLength: newValue.length,
        preview: newValue.slice(0, 100),
      });

      // Save to dataSource with all required field properties preserved
      handleFieldChange(index, {
        id: field.id,
        type: field.type,
        label: field.label,
        name: field.name,
        dataSource: newValue,
        dataSourceType: field.dataSourceType,
        properties: field.properties,
        required: field.required,
      });
    } catch (saveError) {
      logger.error('Error saving markdown field:', saveError);
      setError('Error saving content');
    }
  };

  const handleInsertText = (code: string) => {
    const input = textFieldRef.current?.querySelector('textarea');
    if (!input) return;

    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const text = currentValue;

    // Insert code at cursor position or append if no selection
    const before = text.substring(0, start);
    const after = text.substring(end);
    const newValue = before + (before && !before.endsWith('\n\n') ? '\n\n' : '') + code + '\n\n' + after;

    setCurrentValue(newValue);
    setHasUserChanges(true);

    // Save the updated value
    handleFieldChange(index, {
      id: field.id,
      type: field.type,
      label: field.label,
      name: field.name,
      dataSource: newValue,
      dataSourceType: field.dataSourceType,
      properties: field.properties,
      required: field.required,
    });

    // Focus back on textarea and set cursor position
    setTimeout(() => {
      input.focus();
      const newPosition = start + code.length + (before && !before.endsWith('\n\n') ? 2 : 0) + 2;
      input.setSelectionRange(newPosition, newPosition);
    }, 100);
  };

  const handleInsertExample = (code: string) => {
    handleInsertText(code);
    // Close modal
    setModalOpen(false);
  };

  const handleTabChange = (exampleKey: string, newValue: number) => {
    setExampleTabs((prev) => ({ ...prev, [exampleKey]: newValue }));
  };

  return (
    <>
      <Stack spacing={2}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use the formatting toolbar below or type markdown/HTML directly. Click the help button for more examples.
          </Typography>

          {error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Formatting Toolbar */}
          <MarkdownToolbar onInsert={handleInsertText} disabled={isSubmitting || isUsingExistingField} />

          <TextField
            ref={textFieldRef}
            fullWidth
            label="Markdown Content"
            value={currentValue}
            onChange={handleChange}
            multiline
            minRows={8}
            maxRows={30}
            required
            disabled={isSubmitting || isUsingExistingField}
            helperText={error ? error : 'Enter markdown content for this field. Drag the corner to resize.'}
            error={!!error}
            sx={{
              '& .MuiInputBase-root': {
                alignItems: 'flex-start',
              },
              '& textarea': {
                resize: 'vertical',
                minHeight: '200px',
              },
            }}
          />
        </Box>

        {/* Button to open examples modal */}
        <Button
          variant="outlined"
          startIcon={<HelpOutlineIcon />}
          onClick={() => setModalOpen(true)}
          disabled={isSubmitting || isUsingExistingField}
          sx={{
            textTransform: 'none',
            alignSelf: 'flex-start',
          }}
        >
          View Markdown Examples
        </Button>
      </Stack>

      {/* Markdown Examples Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="bold">
              Markdown Examples
            </Typography>
            <IconButton onClick={() => setModalOpen(false)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Click "Insert" to add any example to your content
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {MARKDOWN_EXAMPLES.map((category, categoryIndex) => (
              <Accordion
                key={categoryIndex}
                defaultExpanded={categoryIndex === 0}
                sx={{
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px !important',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon fontSize="small" />}
                  sx={{
                    minHeight: '40px !important',
                    backgroundColor: '#f8fafc',
                    borderRadius: '6px 6px 0 0',
                    '&:hover': {
                      backgroundColor: '#f0f0f0',
                    },
                    '& .MuiAccordionSummary-content': {
                      my: '8px !important',
                    },
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    {category.category}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: 'white', p: 1.5 }}>
                  <Stack spacing={1.5}>
                    {category.examples.map((example, exampleIndex) => {
                      const exampleKey = `${categoryIndex}-${exampleIndex}`;
                      const currentTab = exampleTabs[exampleKey] || 0;

                      return (
                        <Paper
                          key={exampleIndex}
                          elevation={0}
                          sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <Stack>
                            {/* Example Header */}
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{
                                px: 1.5,
                                py: 1,
                                backgroundColor: '#fafafa',
                                borderBottom: '1px solid #e0e0e0',
                              }}
                            >
                              <Typography variant="body2" fontWeight="600" color="primary">
                                {example.name}
                              </Typography>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                                onClick={() => handleInsertExample(example.code)}
                                sx={{
                                  textTransform: 'none',
                                  fontSize: '0.75rem',
                                  py: 0.5,
                                  px: 1.5,
                                }}
                              >
                                Insert
                              </Button>
                            </Stack>

                            {/* Preview/Code Tabs */}
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                              <Tabs
                                value={currentTab}
                                onChange={(_, newValue) => handleTabChange(exampleKey, newValue)}
                                sx={{
                                  minHeight: '36px',
                                  '& .MuiTab-root': {
                                    minHeight: '36px',
                                    py: 0.5,
                                    fontSize: '0.8rem',
                                  },
                                  px: 1,
                                }}
                              >
                                <Tab label="Preview" sx={{ textTransform: 'none' }} />
                                <Tab label="Code" sx={{ textTransform: 'none' }} />
                              </Tabs>
                            </Box>

                            {/* Tab Content */}
                            <Box sx={{ p: 1.5 }}>
                              {currentTab === 0 ? (
                                // Preview Tab
                                <Box
                                  sx={{
                                    minHeight: 60,
                                    p: 1.5,
                                    backgroundColor: 'white',
                                    border: '1px solid #f0f0f0',
                                    borderRadius: 1,
                                  }}
                                >
                                  <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    rehypePlugins={[rehypeRaw]}
                                    skipHtml={false}
                                    components={getMarkdownComponents()}
                                  >
                                    {example.code}
                                  </ReactMarkdown>
                                </Box>
                              ) : (
                                // Code Tab
                                <Box
                                  sx={{
                                    backgroundColor: '#f5f5f5',
                                    p: 1.5,
                                    borderRadius: 1,
                                    fontFamily: 'monospace',
                                    fontSize: '0.75rem',
                                    overflow: 'auto',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-all',
                                    maxHeight: 250,
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {example.code}
                                </Box>
                              )}
                            </Box>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};
